import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Plus, Search, Loader2, IndianRupee, ArrowUpRight, ArrowDownLeft, TrendingUp, FileSpreadsheet, Pencil, Trash2, Building2, Undo2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { exportToExcel } from '../lib/exportUtils';
import { useConfirmation } from '../components/ConfirmationProvider';
import { formatDate, formatTimeIST } from '../lib/formatUtils';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import TruncatedText from '../components/common/TruncatedText';
import { TransactionFormDialog } from '../components/transactions/TransactionFormDialog';
import { Pagination } from '../components/Pagination';
import { usePageSize } from '../context/PageSizeContext';
import { useDebounce } from '../lib/useDebounce';

const formatCurrency = (a) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(a || 0);

export const TransactionsPage = () => {
  const [transactions, setTransactions] = useState([]);
  const confirm = useConfirmation();
  const { pageSize } = usePageSize();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({});
  const [typeFilter, setTypeFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 350);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  // Bank filter — 'all' = no filter, 'none' = rows with no bank tagged,
  // otherwise a bank_accounts.id. Backend understands the same values.
  const [bankFilter, setBankFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogInitial, setDialogInitial] = useState(null);
  const [editTxn, setEditTxn] = useState(null);

  // Bank accounts for label mapping in the table
  const [bankAccounts, setBankAccounts] = useState([]);

  // Reset to page 1 whenever filters / search change
  useEffect(() => { setPage(1); }, [typeFilter, bankFilter, dateFrom, dateTo, debouncedSearch, pageSize]);

  useEffect(() => { fetchTransactions(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ },
    [typeFilter, bankFilter, dateFrom, dateTo, debouncedSearch, page, pageSize]);

  useEffect(() => {
    const loadBankAccounts = async () => {
      try {
        const res = await api.get('/transactions/bank-accounts');
        if (res.data.success) setBankAccounts(res.data.data);
      } catch {}
    };
    loadBankAccounts();
  }, []);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(pageSize) });
      if (typeFilter !== 'all') params.set('type', typeFilter);
      if (bankFilter !== 'all') params.set('bank_account', bankFilter);
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      if (debouncedSearch) params.set('search', debouncedSearch);
      const res = await api.get(`/transactions?${params.toString()}`);
      if (res.data.success) {
        setTransactions(res.data.data);
        setTotal(res.data.total ?? res.data.data.length);
        setSummary(res.data.summary || {});
      }
    } catch { setTransactions([]); setTotal(0); }
    finally { setLoading(false); }
  };

  const openCreateDialog = (type = 'credit') => {
    setEditTxn(null);
    setDialogInitial({ type });
    setDialogOpen(true);
  };

  const handleEdit = (txn) => {
    setDialogInitial(null);
    setEditTxn(txn);
    setDialogOpen(true);
  };

  const handleDelete = async (txn) => {
    // Salary expenses are linked to a payroll row — deleting directly
    // would leave that payroll row stuck in "paid" with a dangling
    // transaction_id. Route the user to the reverse-payment flow instead,
    // which cleans up both sides atomically.
    if (txn.payroll_id) {
      const ok = await confirm({
        title: 'Reverse Salary Payment?',
        description: `This salary transaction is linked to a payroll record. Reversing it will soft-delete this transaction, credit ${formatCurrency(txn.amount)} back to the bank, and flip the payroll row back to "pending". Proceed?`
      });
      if (!ok) return;
      try {
        const res = await api.post(`/payroll/${txn.payroll_id}/reverse-payment`);
        if (res.data?.success) {
          toast.success(`Reversed ${formatCurrency(res.data.data.amount_refunded || 0)} from the salary ledger`);
          fetchTransactions();
        }
      } catch (e) { toast.error(e.response?.data?.detail || 'Reversal failed'); }
      return;
    }
    const confirmed = await confirm({ title: 'Delete Transaction', description: `Are you sure you want to delete this ${txn.type === 'credit' ? 'credit' : 'debit'} transaction of ${formatCurrency(txn.amount)}? This action cannot be undone.` });
    if (!confirmed) return;
    try {
      await api.delete(`/transactions/${txn.id}`);
      toast.success('Transaction deleted');
      fetchTransactions();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const bankMap = {};
  bankAccounts.forEach(a => { bankMap[a.id] = a.name; });

  // Server-side export — fetches EVERY row matching the current filter
  // set (search, type, date range), not just the visible page. Matches the
  // pattern used by Leaves / Tasks / Payroll exports across the app.
  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ page: '1', limit: '10000' });
      if (typeFilter !== 'all') params.set('type', typeFilter);
      if (bankFilter !== 'all') params.set('bank_account', bankFilter);
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      if (debouncedSearch) params.set('search', debouncedSearch);
      const res = await api.get(`/transactions?${params.toString()}`);
      const rows = res.data?.data || [];
      if (!rows.length) { toast.info('No transactions match the current filters'); return; }
      exportToExcel(rows.map(t => ({
        date: t.date,
        type: t.type,
        description: t.description,
        amount: t.amount,
        category: t.category,
        status: t.status,
        bank_account: bankMap[t.bank_account] || '',
        invoice_no: t.invoice_number || '',
        added_by: t.created_by_name || '',
      })), [
        { key: 'date', header: 'Date' },
        { key: 'type', header: 'Type' },
        { key: 'description', header: 'Description' },
        { key: 'amount', header: 'Amount' },
        { key: 'category', header: 'Category' },
        { key: 'status', header: 'Status' },
        { key: 'bank_account', header: 'Bank' },
        { key: 'invoice_no', header: 'Invoice #' },
        { key: 'added_by', header: 'Added By' },
      ], 'Transactions');
      toast.success(`Exported ${rows.length} transaction(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Export failed');
    } finally { setExporting(false); }
  };

  const filtered = transactions;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="transactions-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div><h1 className="text-2xl font-bold">Transactions</h1><p className="text-muted-foreground">Unified view of all credits (payments) and debits (expenses)</p></div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => exportToExcel(filtered, [
            { key: 'date', header: 'Date' }, { key: 'type', header: 'Type' },
            { key: 'description', header: 'Description' }, { key: 'amount', header: 'Amount' },
            { key: 'category', header: 'Category' }, { key: 'status', header: 'Status' },
            { key: 'created_by_name', header: 'Added By' },
          ], 'Transactions')} data-testid="export-txn-excel"><FileSpreadsheet className="h-4 w-4 mr-1" />Excel</Button>
          <Button onClick={() => openCreateDialog('credit')} data-testid="add-transaction-btn"><Plus className="h-4 w-4 mr-2" />Add Transaction</Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-emerald-500/10 flex items-center justify-center"><ArrowDownLeft className="h-5 w-5 text-emerald-500" /></div><div><p className="text-xl font-bold text-emerald-500" data-testid="txn-total-credit">{formatCurrency(summary.total_credit)}</p><p className="text-xs text-muted-foreground">Total Credits ({summary.credit_count || 0})</p></div></div></CardContent></Card>
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-red-500/10 flex items-center justify-center"><ArrowUpRight className="h-5 w-5 text-red-500" /></div><div><p className="text-xl font-bold text-red-500" data-testid="txn-total-debit">{formatCurrency(summary.total_debit)}</p><p className="text-xs text-muted-foreground">Total Debits ({summary.debit_count || 0})</p></div></div></CardContent></Card>
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center"><TrendingUp className="h-5 w-5 text-primary" /></div><div><p className={`text-xl font-bold ${(() => { const total = (summary.bank_accounts || []).reduce((s, a) => s + (a.current_balance != null ? a.current_balance : a.credit_total || 0), 0); return total >= 0 ? 'text-emerald-500' : 'text-red-500'; })()}`} data-testid="txn-net">{formatCurrency((summary.bank_accounts || []).reduce((s, a) => s + (a.current_balance != null ? a.current_balance : a.credit_total || 0), 0))}</p><p className="text-xs text-muted-foreground">Net Balance</p></div></div></CardContent></Card>
        {(summary.bank_accounts || []).map(acc => (
          <Card key={acc.id} className="stat-card" data-testid={`bank-balance-${acc.id}`}><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-blue-500/10 flex items-center justify-center"><Building2 className="h-5 w-5 text-blue-500" /></div><div><p className="text-xl font-bold text-blue-400">{formatCurrency(acc.current_balance != null ? acc.current_balance : acc.credit_total)}</p><p className="text-xs text-muted-foreground">{acc.name}</p></div></div></CardContent></Card>
        ))}
      </div>

      {/* Filters */}
      <Card><CardContent className="py-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" /><Input placeholder="Search transactions..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10" data-testid="txn-search" /></div>
          <Select value={typeFilter} onValueChange={setTypeFilter}><SelectTrigger className="w-[140px]" data-testid="txn-type-filter"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All Types</SelectItem><SelectItem value="credit">Credits</SelectItem><SelectItem value="debit">Debits</SelectItem></SelectContent></Select>
          <Select value={bankFilter} onValueChange={setBankFilter}>
            <SelectTrigger className="w-[200px]" data-testid="txn-bank-filter"><SelectValue placeholder="All Banks" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Banks</SelectItem>
              <SelectItem value="none">— No bank tagged</SelectItem>
              {bankAccounts.map(b => (
                <SelectItem key={b.id} value={b.id}>
                  {b.name || `${b.bank || 'Bank'} - ${(b.account_number || '').slice(-4)}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-[160px]" placeholder="From" data-testid="txn-date-from" />
          <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-[160px]" placeholder="To" data-testid="txn-date-to" />
        </div>
      </CardContent></Card>

      {/* Table */}
      <Card><CardContent className="p-0">
        {loading ? <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div> :
        filtered.length === 0 ? <div className="flex flex-col items-center justify-center h-64 text-muted-foreground"><IndianRupee className="h-12 w-12 mb-4 opacity-50" /><p>No transactions found</p></div> :
        <Table data-testid="transactions-table"><TableHeader><TableRow><TableHead className="w-[50px]"></TableHead><TableHead>Date</TableHead><TableHead>Description</TableHead><TableHead>Category</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Status</TableHead><TableHead className="text-xs">Added By</TableHead><TableHead className="w-[60px]">Actions</TableHead></TableRow></TableHeader>
        <TableBody>{filtered.map(t => (
          <TableRow key={t.id} data-testid={`txn-row-${t.id}`}>
            <TableCell>
              <div className={`h-8 w-8 rounded-full flex items-center justify-center ${t.type === 'credit' ? 'bg-emerald-500/10' : 'bg-red-500/10'}`}>
                {t.type === 'credit' ? <ArrowDownLeft className="h-4 w-4 text-emerald-500" /> : <ArrowUpRight className="h-4 w-4 text-red-500" />}
              </div>
            </TableCell>
            <TableCell className="text-sm">
              <div>{formatDate(t.date)}</div>
              {t.created_at && (
                <div className="text-[10px] text-muted-foreground"
                     data-testid={`txn-time-${t.id}`}>
                  {formatTimeIST(t.created_at)}
                </div>
              )}
            </TableCell>
            <TableCell>
              <div>
              {t.project_name && (
                <Link
                  to={
                    // Hosting + AMC don't have per-id detail pages yet —
                    // deep-link to the list page with the project name as
                    // a search query so the row is visible at top. Falls
                    // back to /projects/{id} for legacy project-tied
                    // transactions which DO have a detail page.
                    t.entity_type === 'hosting' ? `/hosting?q=${encodeURIComponent(t.project_name)}`
                    : t.entity_type === 'amc' ? `/amc?q=${encodeURIComponent(t.project_name)}`
                    : t.project_id ? `/projects/${t.project_id}`
                    : '#'
                  }
                  className="block font-semibold text-sm text-foreground hover:text-primary hover:underline truncate max-w-[260px]"
                  title={`Jump to ${t.project_name}`}
                  data-testid={`txn-project-${t.id}`}
                >
                  {t.project_name}
                </Link>
              )}
              <TruncatedText
                text={t.description}
                lines={2}
                className={`text-xs max-w-[260px] ${t.project_name ? 'text-muted-foreground' : 'font-medium text-sm'}`}
              />
              {t.tds_amount > 0 && <p className="text-xs text-orange-400">TDS: {formatCurrency(t.tds_amount)}</p>}
              {t.bank_txn_amount > 0 && <p className="text-xs text-blue-400">Bank Txn: {formatCurrency(t.bank_txn_amount)}</p>}
              {t.bank_account && bankMap[t.bank_account] && (
                <span className="inline-flex items-center gap-1 mt-1 text-[10px] px-1.5 py-0.5 rounded bg-muted/40 text-muted-foreground border border-border/50">
                  <Building2 className="h-2.5 w-2.5" />{bankMap[t.bank_account]}
                </span>
              )}
              </div>
            </TableCell>
            <TableCell><Badge variant="outline" className="text-xs">{t.category}</Badge></TableCell>
            <TableCell className={`text-right font-semibold ${t.type === 'credit' ? 'text-emerald-500' : 'text-red-500'}`}>
              {t.type === 'credit' ? '+' : '-'}{formatCurrency(t.amount)}
            </TableCell>
            <TableCell><Badge className={t.status === 'completed' || t.status === 'approved' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-amber-500/10 text-amber-500'}>{t.status}</Badge></TableCell>
            <TableCell>
              <span
                className={`text-[11px] ${t.created_by_name?.startsWith('System') || t.created_by_name?.startsWith('Customer') ? 'text-muted-foreground italic' : 'text-foreground'}`}
                data-testid={`txn-added-by-${t.id}`}
                title={t.created_by || 'Unknown'}
              >
                {t.created_by_name || '—'}
              </span>
            </TableCell>
            <TableCell>
              <DropdownMenu>
                <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`txn-actions-${t.id}`}><Pencil className="h-3.5 w-3.5" /></Button></DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {t.source_smb_lead_id && (
                    <DropdownMenuItem
                      onClick={() => window.open(`/smb/leads/${t.source_smb_lead_id}`, '_blank')}
                      data-testid={`open-smb-lead-${t.id}`}
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />Open SMB Lead
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => handleEdit(t)} data-testid={`edit-txn-${t.id}`}><Pencil className="h-4 w-4 mr-2" />Edit</DropdownMenuItem>
                  {t.payroll_id ? (
                    <DropdownMenuItem className="text-amber-600 focus:text-amber-600" onClick={() => handleDelete(t)} data-testid={`reverse-txn-${t.id}`}><Undo2 className="h-4 w-4 mr-2" />Reverse Payment</DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem className="text-destructive" onClick={() => handleDelete(t)} data-testid={`delete-txn-${t.id}`}><Trash2 className="h-4 w-4 mr-2" />Delete</DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </TableCell>
          </TableRow>))}</TableBody></Table>}
      </CardContent></Card>

      {!loading && total > 0 && (
        <Pagination
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
          testIdPrefix="transactions-pagination"
        />
      )}

      <TransactionFormDialog
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); setEditTxn(null); setDialogInitial(null); }}
        initialValues={dialogInitial}
        editTxn={editTxn}
        onSaved={() => fetchTransactions()}
      />
    </div>
  );
};
